import { useFormatter } from '@api/useFormatter'
import { addCommasToNumber, BBDTag, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Col, Row, Table } from 'antd'
import _ from 'lodash'
import React from 'react'
import { Link } from 'react-router-dom'
import { PayrollIcon } from '../../Payroll/PayrollIcon'

export function PayConfigDetail({ details }) {
  const groups = _.groupBy(details, 'grouping')
  const groupKeys = Object.keys(groups)

  // Create pairs of groups
  const groupPairs = []
  for (let i = 0; i < groupKeys.length; i += 2) {
    groupPairs.push(groupKeys.slice(i, i + 2))
  }

  return (
    <div className='p-3 bg-2' style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {groupPairs.map((pair, pairIndex) => (
        <Horizontal gap={16} key={pairIndex} style={{ width: '100%' }}>
          {pair.map((groupKey, index) => (
            <ConfigGroup key={index} name={groupKey} data={groups[groupKey]} style={{ flex: 1 }} />
          ))}
        </Horizontal>
      ))}
    </div>
  )
}

function ConfigGroup({ name, data }) {
  const { formatCurrency, formatVolume, formatDistance } = useFormatter()
  const formattedCurrency = formatCurrency(null, true)
  const formattedVolume = formatVolume(null, false)
  const formattedDistance = formatDistance(null, false)

  return (
    <Vertical gap={8}>
      <div className='section pt-2 pb-3'>
        <PayrollIcon name={data[0].unit} />
        <b className='group pl-2'>{name}</b>
      </div>
      <Table
        style={{ fontSize: 10 }}
        columns={columns(formattedCurrency, formattedVolume, formattedDistance)}
        dataSource={data}
        pagination={false}
      />
    </Vertical>
  )
}

const columns = (formattedCurrency, formattedVolume, formattedDistance) => [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    render: (value) => <Texto weight='bold'>{value}</Texto>,
  },
  {
    title: 'Active',
    dataIndex: 'disabled',
    key: 'disabled',
    render: (value) => {
      return value ? <BBDTag>Disabled</BBDTag> : <BBDTag theme2>Active</BBDTag>
    },
  },
  {
    title: 'Bonus Threshold',
    dataIndex: 'threshold',
    key: 'threshold',
    render: (threshold, row) => {
      if (row.name.includes('Longevity Bonus')) {
        return (
          <Texto category='label' appearance='primary'>
            <b>{addCommasToNumber(threshold)}</b> {row.threshold_interval?.toLowerCase()}
          </Texto>
        )
      }
      return threshold && row.unit !== 'percent' ? ( // dont show bonus threshold for percent unit - added for deemed hours and experience bonuses
        <Horizontal style={{ overflow: 'visible' }}>
          <Texto category='label' appearance='primary'>
            <b className='threshold-value'>{addCommasToNumber(threshold)}</b>
            <span className='threshold-value unit pl-1'>{row.unit}</span>
            {row.threshold_interval && (
              <span className='threshold-value unit pl-1'>{`per ${row.threshold_interval?.toLowerCase()}`}</span>
            )}
          </Texto>
        </Horizontal>
      ) : (
        <Texto appearance='hint' category='label'>
          N/A
        </Texto>
      )
    },
  },
  {
    title: 'Rate',
    dataIndex: 'rate',
    key: 'rate',
    align: 'right',
    render: (value, row) => {
      const formattedUnit = () => {
        switch (row.unit) {
          case 'miles':
            return formattedDistance
          case 'gallons':
            return formattedVolume
          case 'dollars':
            return formattedCurrency
          default:
            return row.unit
        }
      }
      if (row.driver_rates && row.driver_rates?.length > 0) {
        return (
          <Link
            target='_blank'
            to={`/driver/pay_mileage_bands?${new URLSearchParams({ table_id: row.driver_rates[0].id })}`}
          >
            {row.driver_rates[0].name}
          </Link>
        )
      }
      if (row.unit === 'percent') {
        return (
          <Texto style={{ textAlign: 'right' }}>
            <b>{parseFloat(value) * 100}% </b>/ {row.pay_type}
          </Texto>
        )
      }
      return (
        <Horizontal justifyContent='flex-end'>
          <Texto align='right'>
            <b>
              {formattedCurrency}
              {value}
            </b>
            /{formattedUnit()}
          </Texto>
          {!!row.minimum_pay && (
            <Texto className='pl-2'>{`(Minimum: ${formattedCurrency}${row.minimum_pay.toFixed(2)})`}</Texto>
          )}
        </Horizontal>
      )
    },
  },
]
